import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  ArrowLeft,
  Copy,
  Users,
  Building2,
  DollarSign,
  TrendingUp,
  RefreshCw,
  Loader2,
  CheckCircle2,
  Clock,
  Share2,
  ExternalLink,
  Percent,
  Wallet,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export default function PartnerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  const [payoutForm, setPayoutForm] = useState({ payment_method: 'bank_transfer', notes: '' });
  const [submittingPayout, setSubmittingPayout] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchPayouts();
  }, []);

  const fetchPayouts = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const r = await fetch(`${API_URL}/api/partner-program/my-payout-requests`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (r.ok) setPayoutRequests(await r.json());
    } catch (e) {
      // silent
    }
  };

  const submitPayoutRequest = async () => {
    setSubmittingPayout(true);
    try {
      const token = localStorage.getItem('session_token');
      const r = await fetch(`${API_URL}/api/partner-program/payout-requests`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(payoutForm),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to submit payout request');
      }
      toast.success('Payout request submitted for approval');
      setShowPayoutDialog(false);
      setPayoutForm({ payment_method: 'bank_transfer', notes: '' });
      fetchPayouts();
      fetchDashboardData();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmittingPayout(false);
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/partner-program/my-dashboard`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch partner dashboard');
      const data = await response.json();
      setDashboardData(data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load partner dashboard');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    if (dashboardData?.partner?.referral_code) {
      navigator.clipboard.writeText(dashboardData.partner.referral_code);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyReferralLink = () => {
    if (dashboardData?.partner?.referral_code) {
      const link = `${window.location.origin}/signup?ref=${dashboardData.partner.referral_code}`;
      navigator.clipboard.writeText(link);
      toast.success('Referral link copied!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const { partner, stats, referrals, monthly_breakdown } = dashboardData || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/agent')} data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Partner Program</h1>
                <p className="text-sm text-slate-500">Earn commissions on referrals</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={fetchDashboardData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Referral Code Card */}
        <Card className="border-0 shadow-lg mb-8 bg-gradient-to-r from-blue-600 to-emerald-500" data-testid="referral-code-card">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="text-white">
                <p className="text-blue-100 text-sm mb-1">Your Referral Code</p>
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-bold font-mono tracking-wider">
                    {partner?.referral_code || 'N/A'}
                  </span>
                  <Button 
                    variant="secondary" 
                    size="sm" 
                    onClick={copyReferralCode}
                    className="bg-white/20 hover:bg-white/30 text-white"
                    data-testid="copy-code-btn"
                  >
                    {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-blue-100 text-sm mt-2">
                  {stats?.commission_rate}% commission on all referred subscriptions
                </p>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="secondary"
                  className="bg-white text-blue-600 hover:bg-blue-50"
                  onClick={copyReferralLink}
                  data-testid="copy-link-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Copy Signup Link
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-0 shadow-lg" data-testid="complexes-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <h3 className="text-sm font-medium text-slate-500 mb-1">Complexes Onboarded</h3>
              <p className="text-3xl font-bold text-slate-900">{stats?.total_complexes || 0}</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg" data-testid="units-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
              <h3 className="text-sm font-medium text-slate-500 mb-1">Total Units</h3>
              <p className="text-3xl font-bold text-slate-900">{stats?.total_units || 0}</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg" data-testid="revenue-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <h3 className="text-sm font-medium text-slate-500 mb-1">Subscription Revenue</h3>
              <p className="text-3xl font-bold text-slate-900">
                {formatCurrency(stats?.total_subscription_revenue || 0)}
              </p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg" data-testid="commission-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-amber-600" />
                </div>
              </div>
              <h3 className="text-sm font-medium text-slate-500 mb-1">Commission Earned</h3>
              <p className="text-3xl font-bold text-slate-900">
                {formatCurrency(stats?.total_commission_earned || 0)}
              </p>
              <div className="flex gap-4 mt-2 text-sm">
                <span className="text-amber-600">
                  <Clock className="w-3 h-3 inline mr-1" />
                  Pending: {formatCurrency(stats?.pending_commission || 0)}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Payout Requests */}
        <Card className="border-0 shadow-lg mb-8" data-testid="payouts-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Wallet className="w-5 h-5 text-indigo-600" />
                Payout Requests
              </CardTitle>
              <CardDescription>Request a payout of your pending commission earnings</CardDescription>
            </div>
            <Button
              onClick={() => setShowPayoutDialog(true)}
              disabled={!stats?.pending_commission || stats.pending_commission <= 0}
              data-testid="request-payout-btn"
            >
              <Wallet className="w-4 h-4 mr-2" />
              Request Payout
            </Button>
          </CardHeader>
          <CardContent>
            {payoutRequests.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No payout requests yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-2 text-slate-500">Requested</th>
                      <th className="text-right py-2 px-2 text-slate-500">Amount</th>
                      <th className="text-center py-2 px-2 text-slate-500">Method</th>
                      <th className="text-center py-2 px-2 text-slate-500">Status</th>
                      <th className="text-left py-2 px-2 text-slate-500">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payoutRequests.map(p => (
                      <tr key={p.request_id} className="border-b border-slate-100" data-testid={`payout-row-${p.request_id}`}>
                        <td className="py-2 px-2">{new Date(p.created_at).toLocaleDateString()}</td>
                        <td className="py-2 px-2 text-right font-medium">{formatCurrency(p.total_amount)}</td>
                        <td className="py-2 px-2 text-center capitalize">{p.payment_method?.replace('_', ' ')}</td>
                        <td className="py-2 px-2 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            p.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                            p.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {p.status === 'pending_approval' ? 'Pending' : p.status}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-slate-500 text-xs">
                          {p.rejection_reason || p.approval_note || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Commission Trends */}
          <Card className="border-0 shadow-lg" data-testid="commission-chart">
            <CardHeader>
              <CardTitle className="text-lg">Commission Trends</CardTitle>
              <CardDescription>Monthly commission earned</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthly_breakdown || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" stroke="#64748b" />
                    <YAxis stroke="#64748b" tickFormatter={(v) => `R${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="commission" name="Commission" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Referrals Over Time */}
          <Card className="border-0 shadow-lg" data-testid="referrals-chart">
            <CardHeader>
              <CardTitle className="text-lg">Referral Activity</CardTitle>
              <CardDescription>New complexes referred per month</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthly_breakdown || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" stroke="#64748b" />
                    <YAxis stroke="#64748b" />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="referrals" 
                      name="Referrals" 
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      dot={{ fill: '#3b82f6' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Referred Complexes Table */}
        <Card className="border-0 shadow-lg" data-testid="referrals-table">
          <CardHeader>
            <CardTitle className="text-lg">Referred Complexes</CardTitle>
            <CardDescription>Buildings signed up using your referral code</CardDescription>
          </CardHeader>
          <CardContent>
            {referrals && referrals.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Complex</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Units</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Subscription</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Commission</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Referred</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.map((referral) => (
                      <tr key={referral.referral_id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <div className="font-medium text-slate-900">{referral.hoa_name}</div>
                        </td>
                        <td className="py-3 px-4 text-right text-slate-600">{referral.unit_count}</td>
                        <td className="py-3 px-4 text-right text-slate-900 font-medium">
                          {formatCurrency(referral.subscription_amount)}
                        </td>
                        <td className="py-3 px-4 text-right text-emerald-600 font-medium">
                          {formatCurrency(referral.subscription_amount * (stats?.commission_rate || 10) / 100)}
                        </td>
                        <td className="py-3 px-4 text-slate-500 text-sm">
                          {new Date(referral.referred_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            referral.status === 'active' 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {referral.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Share2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No referrals yet</h3>
                <p className="text-slate-500 mb-4">Share your referral code to start earning commissions</p>
                <Button onClick={copyReferralLink}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Referral Link
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Request Payout Dialog */}
      <Dialog open={showPayoutDialog} onOpenChange={setShowPayoutDialog}>
        <DialogContent data-testid="payout-dialog">
          <DialogHeader>
            <DialogTitle>Request Payout</DialogTitle>
            <DialogDescription>
              You are about to request a payout of <strong>{formatCurrency(stats?.pending_commission || 0)}</strong> covering all pending commissions. The platform admin will review and approve.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Payment Method</Label>
              <select
                value={payoutForm.payment_method}
                onChange={(e) => setPayoutForm({ ...payoutForm, payment_method: e.target.value })}
                className="mt-1 w-full p-2 border rounded"
                data-testid="payout-method"
              >
                <option value="bank_transfer">Bank Transfer (EFT)</option>
                <option value="payfast">PayFast</option>
                <option value="paypal">PayPal</option>
              </select>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                rows={3}
                value={payoutForm.notes}
                onChange={(e) => setPayoutForm({ ...payoutForm, notes: e.target.value })}
                placeholder="Bank account details, PayPal email, or any extra info for the admin..."
                data-testid="payout-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayoutDialog(false)}>Cancel</Button>
            <Button onClick={submitPayoutRequest} disabled={submittingPayout} data-testid="payout-submit">
              {submittingPayout ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wallet className="w-4 h-4 mr-2" />}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
